import { Fragment, createContext, useContext } from "react";
import { useInRouterContext } from "react-router";

import { Link } from "../../../general/Link";
import { Tag } from "../../../general/Tag";
import { Bold } from "../../../typography/Bold";
import { Heading } from "../../../typography/Heading";
import { Italic } from "../../../typography/Italic";
import { Paragraph } from "../../../typography/Paragraph";
import { Regular } from "../../../typography/Regular";
import { Strikethrough } from "../../../typography/Strikethrough";
import { Underlined } from "../../../typography/Underlined";
import type { RTEBaseDefs, RTEChildDefs, RTEComponentDefs, RTEElementDefs, RTEFormatOpts } from "../RichTextEditorProps";
import { getHtmlDefsFromBaseProps } from "../utils/getHtmlDefsFromBaseProps";
import { parseComponentsEnvelope } from "../utils/parseComponentsEnvelope";
import { isSafeHref } from "../utils/urlSafety";

export type RTERenderLinkDefsExt = (props: { readonly href: string; readonly children: RTEElementDefs }) => RTEElementDefs;

export interface RTEDisplayDefs extends RTEBaseDefs {
  readonly value: string | null;
  readonly renderLink?: RTERenderLinkDefsExt;
}

const RenderLinkContext = createContext<RTERenderLinkDefsExt | undefined>(undefined);

const wrapWithFormats = (content: string, formats: ReadonlyArray<RTEFormatOpts>): RTEElementDefs => {
  let node: RTEElementDefs = <Regular>{content}</Regular>;
  if (formats.includes("strikethrough")) node = <Strikethrough>{node}</Strikethrough>;
  if (formats.includes("underline")) node = <Underlined>{node}</Underlined>;
  if (formats.includes("italic")) node = <Italic>{node}</Italic>;
  if (formats.includes("bold")) node = <Bold>{node}</Bold>;
  return node;
};

const RichTextLinkWrapper: RTEComponentDefs<{ href: string; children: RTEElementDefs }> = ({ href, children }) => {
  const renderLink = useContext(RenderLinkContext);
  const inRouterContext = useInRouterContext();
  if (!isSafeHref(href)) return <>{children}</>;
  if (renderLink) return <>{renderLink({ href, children })}</>;
  if (inRouterContext) return <Link to={href}>{children}</Link>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const renderNode = (node: RTEChildDefs, key: number): RTEElementDefs => {
  switch (node.type) {
    case "linebreak":
      return <br key={key} />;
    case "text":
      return <Fragment key={key}>{wrapWithFormats(node.content, node.formats)}</Fragment>;
    case "link":
      return (
        <RichTextLinkWrapper key={key} href={node.href}>
          {node.children.map(renderChild)}
        </RichTextLinkWrapper>
      );
    case "paragraph":
      return (
        <Paragraph key={key} textAlign={node.textAlign}>
          {node.children.map(renderChild)}
        </Paragraph>
      );
    case "heading":
      return (
        <Heading key={key} level={node.level} textAlign={node.textAlign}>
          {node.children.map(renderChild)}
        </Heading>
      );
    case "list": {
      if (node.listType === "number") {
        return (
          <ol key={key} className="list ordered">
            {node.children.map(renderListItem)}
          </ol>
        );
      }
      return (
        <ul key={key} className="list unordered">
          {node.children.map(renderListItem)}
        </ul>
      );
    }
    case "listitem":
      return (
        <li key={key} className="listitem">
          {node.children.map(renderChild)}
        </li>
      );
    case "code":
      return (
        <Fragment key={key}>
          &nbsp;
          <Tag>
            <code>{node.content}</code>
          </Tag>
          &nbsp;
        </Fragment>
      );
    default:
      return null;
  }
};

const renderChild = (child: RTEChildDefs, key: number): RTEElementDefs => {
  return renderNode(child, key);
};

const renderListItem = (item: RTEChildDefs, key: number): RTEElementDefs => {
  return renderNode(item, key);
};

export const Display: RTEComponentDefs<RTEDisplayDefs> = (props) => {
  const { value } = props;
  const htmlDefs = getHtmlDefsFromBaseProps(props, { className: ["rich-text-display"] });

  if (!value) return null;

  const envelope = parseComponentsEnvelope(value);
  if (!envelope) return null;

  return (
    <RenderLinkContext.Provider value={props.renderLink}>
      <div {...htmlDefs}>{envelope.nodes.map(renderNode)}</div>
    </RenderLinkContext.Provider>
  );
};
